<template>
    <div class="fixed inset-0 z-[999]" v-if="showMap">
        <Teleport to="body">
            <Suspense>
                <Map
                    :loading="loading"
                    :showMap="showMap"
                    :mapMarkers="
                        activeFavoritesTab === 'Lounges'
                            ? mapMarkers
                            : airportMapMarkers
                    "
                    :mapType="mapType"
                    :mapCenter="mapCenter"
                    @closeMap="() => setShowMap(false)"
                >
                    <template
                        #popup="{ closeHandler, infoWindowContent, show }"
                    >
                        <LoungeInfoWindow
                            v-if="activeFavoritesTab === 'Lounges'"
                            :infoWindowContent="infoWindowContent"
                            :show="show"
                            @close="closeHandler"
                            class="z-[9999]"
                        />
                        <AirportInfoWindow
                            v-if="activeFavoritesTab === 'Airports'"
                            :infoWindowContent="infoWindowContent"
                            :show="show"
                            @close="closeHandler"
                            class="z-[9999]"
                        />
                    </template>
                </Map>
            </Suspense>
        </Teleport>
    </div>
    <div
        class="w-full h-full flex-col justify-start items-start flex @container relative px-12"
    >
        <!-- FAVORITES HEADER -->
        <h1
            class="flex-col justify-start items-start flex text-slate-800 text-2xl font-semibold leading-loose"
        >
            Favorites
        </h1>
        <!-- END FAVORITES HEADER -->
        <!-- AIRPORTS / LOUNGES TABS -->
        <div
            class="w-full pt-[18px] border-b border-slate-200 justify-start items-start gap-8 flex"
        >
            <button
                class="py-3.5 justify-start items-start gap-2 flex text-slate-800 text-sm font-medium focus:font-semibold leading-tight focus:outline-none focus:ring-0 active:ring-0 cursor-pointer"
                :class="{
                    'border-b-2 border-[#fd421d]':
                        activeFavoritesTab === 'Lounges',
                }"
                @click.prevent="handleChangeTab('Lounges')"
            >
                Lounges
            </button>
            <button
                class="py-3.5 justify-start items-start gap-2 flex text-slate-800 text-sm font-medium focus:font-semibold leading-tight focus:outline-none focus:ring-0 active:ring-0 cursor-pointer"
                :class="{
                    'border-b-2 border-[#fd421d]':
                        activeFavoritesTab === 'Airports',
                }"
                @click.prevent="handleChangeTab('Airports')"
            >
                Airports
            </button>
        </div>
        <!-- END TABS -->
        <!-- START FAVORITES ACTION BAR -->
        <ActionBar @groupingChange="handleGroupingChange" />
        <!-- END FAVORITES ACTION BAR -->
        <!-- START FAVORITE LOUNGES -->
        <div class="w-full flex flex-col relative @container">
            <section
                v-if="!loading && activeFavoritesTab === 'Lounges'"
                v-for="country in sortedFavoriteLounges"
                :key="Object.keys(country)[0]"
                class="pt-4 pb-4 flex-col justify-start items-start flex"
            >
                <h3
                    v-if="grouped"
                    class="text-slate-800 text-lg font-medium font-['Inter'] leading-7 pt-3 pb-4"
                >
                    {{ Object.keys(country)[0] }}
                </h3>
                <transition-group
                    name="lounges"
                    class="w-full justify-between items-start grid grid-cols-1 @lg:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4 gap-x-6 gap-y-8"
                    tag="div"
                >
                    <div
                        v-for="lounge in Object.values(country)[0]"
                        :key="`lounge-${lounge.id}`"
                    >
                        <LoungeCard :lounge="lounge" :hideHours="true" />
                        <p
                            v-if="groupBy?.label !== 'Airport'"
                            class="self-stretch text-slate-500 font-normal text-left text-sm leading-5 m-0 relative"
                        >
                            {{ lounge.airport.code }}
                        </p>
                    </div>
                </transition-group>
            </section>
        </div>
        <!-- START FAVORITE AIRPORTS -->
        <section
            v-if="!loading && activeFavoritesTab === 'Airports'"
            v-for="country in sortedFavoriteAirports"
            :key="Object.keys(country)[0]"
            class="w-full pt-4 pb-4 flex-col justify-start items-start flex @container"
        >
            <h3
                v-if="grouped"
                class="text-slate-800 text-lg font-medium font-['Inter'] leading-7 pt-3 pb-4"
            >
                {{ Object.keys(country)[0] }}
            </h3>
            <transition-group
                name="airports"
                class="w-full justify-between items-start grid grid-cols-1 @lg:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4 gap-x-6 gap-y-8"
                tag="div"
            >
                <AirportCard
                    v-for="airport in Object.values(country)[0]"
                    :airport="airport"
                    :key="`airport-${airport.id}`"
                />
            </transition-group>
        </section>
        <!-- END FAVORITES RESULTS -->
        <!-- START EMPTY STATE -->
        <section
            v-if="!loading && showEmptyState"
            class="w-full flex items-center justify-center"
        >
            <EmptyState
                class="md:rounded-lg mb-5"
                image-name="search.svg"
                :title="emptyStateTitle"
                :description="emptyStateDescription"
                :hasAction="true"
                :actionLabel="
                    favoritesSearchTerm
                        ? 'Reset search criteria'
                        : 'Go to lounges search'
                "
                @action="handleAction"
                v-cloak
            >
                <template #image>
                    <div
                        class="w-16 h-16 bg-slate-200 rounded-full justify-center items-center gap-2.5 inline-flex text-slate-600"
                    >
                        <div class="w-8 h-8 flex items-center justify-center">
                            <svg
                                v-if="favoritesSearchTerm"
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    id="vector"
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M10.998 4.99951C7.68434 4.99951 4.99805 7.6858 4.99805 10.9995C4.99805 14.3132 7.68434 16.9995 10.998 16.9995C14.3118 16.9995 16.998 14.3132 16.998 10.9995C16.998 7.6858 14.3118 4.99951 10.998 4.99951ZM2.99805 10.9995C2.99805 6.58123 6.57977 2.99951 10.998 2.99951C15.4163 2.99951 18.998 6.58123 18.998 10.9995C18.998 12.8482 18.371 14.5505 17.3179 15.9052L20.7051 19.2924C21.0957 19.6829 21.0957 20.3161 20.7051 20.7066C20.3146 21.0971 19.6815 21.0971 19.2909 20.7066L15.9037 17.3194C14.549 18.3724 12.8468 18.9995 10.998 18.9995C6.57977 18.9995 2.99805 15.4178 2.99805 10.9995Z"
                                    fill="currentColor"
                                />
                            </svg>
                            <svg
                                v-else
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    id="vector"
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M16.7343 5.59133C15.4963 5.31528 14.0265 5.63779 12.7125 6.9706C12.5246 7.16122 12.2681 7.26854 12.0004 7.26854C11.7327 7.26854 11.4762 7.16122 11.2883 6.9706C9.97431 5.63779 8.50458 5.31528 7.26649 5.59133C6.00355 5.87293 4.88742 6.79708 4.34587 8.14741C3.3302 10.68 4.25311 15.3399 12.0005 19.8486C19.7478 15.3399 20.6707 10.68 19.655 8.1474C19.1134 6.79707 17.9973 5.87292 16.7343 5.59133ZM21.5112 7.40294C23.1154 11.4028 21.0173 17.0927 12.4894 21.8725C12.1857 22.0428 11.8153 22.0428 11.5115 21.8725C2.98361 17.0927 0.885443 11.4029 2.48959 7.40295C3.26845 5.46089 4.90318 4.06917 6.83124 3.63927C8.52876 3.26077 10.3838 3.64222 12.0004 4.92553C13.617 3.64222 15.4721 3.26077 17.1696 3.63926C19.0977 4.06916 20.7324 5.46087 21.5112 7.40294Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </div>
                    </div>
                </template>
            </EmptyState>
        </section>
        <!-- END EMPTY STATE -->
        <!-- START LOADING STATE -->
        <section class="relative w-full min-h-32">
            <LoadingState :loading="loading" />
        </section>
        <!-- END LOADING STATE -->
    </div>
</template>

<script setup>
import { computed, ref, onBeforeUnmount, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLoungesStore } from '@/store/lounges';
import { useRouter } from 'vue-router';
import ActionBar from '@/components/tools/lounges/partials/lounge-favorites-action-bar/lounge-favorites-action-bar.vue';
import AirportCard from '@/components/tools/lounges/partials/airport-card/airport-card.vue';
import AirportInfoWindow from '@/components/tools/lounges/partials/airport-infowindow/airport-infowindow.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import LoungeCard from '@/components/tools/lounges/partials/lounge-card/lounge-card.vue';
import LoungeInfoWindow from '@/components/tools/lounges/partials/lounge-infowindow/lounge-infowindow.vue';
import Map from '@/components/tools/lounges/partials/lounge-map/lounge-map.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
const loungesStore = useLoungesStore();
const router = useRouter();
const {
    activeAirport,
    activeFavoritesTab,
    favoritesSearchTerm,
    groupBy,
    loading,
    mapCenter,
    mapMarkers,
    mapType,
    showMap,
    sortedFavoriteAirports,
    sortedFavoriteLounges,
} = storeToRefs(loungesStore);
const {
    setActiveFavoritesTab,
    setFavoritesSearchTerm,
    getUserFavorites,
    setGroupBy,
    setShowMap,
} = loungesStore;
const grouped = ref(false);
const airportMapMarkers = ref([]);

const emptyStateTitle = computed(() => {
    const searched = favoritesSearchTerm.value;

    if (searched) {
        return 'No results found matching your search term!';
    } else {
        return `No favorite ${activeFavoritesTab.value.toLowerCase()} yet!`;
    }
});

const emptyStateDescription = computed(() => {
    const searched = favoritesSearchTerm.value;

    if (!searched) {
        return `Tap the heart on any ${activeFavoritesTab.value.toLowerCase()} to save it to your favorites.`;
    }
    return '';
});

const showEmptyState = computed(() => {
    return (
        (activeFavoritesTab.value === 'Airports' &&
            !sortedFavoriteAirports.value.length) ||
        (activeFavoritesTab.value === 'Lounges' &&
            !sortedFavoriteLounges.value.length)
    );
});

const handleGroupingChange = (isGrouped) => {
    grouped.value = isGrouped;
};

const handleChangeTab = (tab) => {
    grouped.value = false;
    setGroupBy(null);
    setActiveFavoritesTab(tab);
};

const handleAction = () => {
    if (favoritesSearchTerm.value) {
        setFavoritesSearchTerm('');
    } else {
        router.push('/lounges');
    }
};

watch(
    () => activeFavoritesTab.value,
    (tab) => {
        if (tab === 'Airports') {
            setGroupBy({ value: 'country', label: 'Country' });
            grouped.value = true;
        }
    },
    { immediate: true }
);

watch(
    () => activeFavoritesTab.value,
    (tab) => {
        if (tab === 'Airports') {
            const arr = [];
            let flattenedResults = [];

            const pushMarker = (item) => {
                const name = item.name?.short || item.name;
                arr.push([
                    name,
                    parseFloat(item.latitude),
                    parseFloat(item.longitude),
                    item,
                ]);
            };

            sortedFavoriteAirports.value.forEach((group) => {
                flattenedResults.push(...Object.values(group));
            });
            flattenedResults.flat().forEach(pushMarker);
            airportMapMarkers.value = arr;
        }
    }
);

onBeforeUnmount(() => {
    setGroupBy(null);
});

getUserFavorites('lounge');
getUserFavorites('airport');
</script>

<style lang="scss" scoped>
.airports-move,
.airports-enter-active {
    transition: all 2.5s ease;
}

.airports-move {
    transition: transform 2.5s ease;
}
.airports {
    transition: all 500ms ease;
}

.airports-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.airports-enter-from {
    opacity: 0;
    position: relative;
    transform: translateX(-30px);
}

.airports-leave-active {
    position: absolute;
    width: 100%;
    transition:
        all 2.5s ease,
        height 0.5s ease,
        margin 0.5s ease;
}

.group-container {
    scroll-snap-type: y mandatory;
}
</style>
